/**
 * Ressources d'une leçon : liens externes et vidéos embarquées (docs/11 §4).
 *
 * L'`<iframe>` reçoit `metadata.embed_url`, **construite par le serveur** à
 * partir du fournisseur et de l'identifiant reconnus — jamais `url`, qui est la
 * chaîne telle que l'enseignant l'a collée. C'est cette indirection qui fait
 * qu'aucun domaine hors allowlist ne peut se retrouver dans un cadre de la page.
 *
 * Les `pdf` et `image` (phase 8) pointent vers **notre** route de
 * téléchargement, jamais vers le magasin d'objets. C'est elle qui vérifie que
 * l'élève a accès au cours avant de rediriger vers une URL présignée de cinq
 * minutes ; construire l'URL du bucket ici court-circuiterait cette garde — et
 * pour un fichier privé, l'URL ne serait de toute façon pas signée.
 *
 * Un PDF est proposé en **téléchargement** et non affiché dans un cadre :
 * docs/09 §6 refuse de rendre un PDF inline dans le domaine de l'application,
 * et la réponse présignée force d'ailleurs `Content-Disposition: attachment`.
 */
import { useTranslation } from "react-i18next";

import type { CourseResource } from "@/features/catalog/api/useCatalog";
import { downloadUrl } from "@/features/storage/api/useStorage";
import { Button } from "@/shared/ui/Button";

interface ResourceListProps {
  resources: CourseResource[];
  onRemove?: (resourceId: string) => void;
}

function embedUrl(resource: CourseResource): string | null {
  const value = resource.metadata["embed_url"];
  return typeof value === "string" ? value : null;
}

export function ResourceList({ resources, onRemove }: ResourceListProps) {
  const { t } = useTranslation("catalog");

  if (resources.length === 0) {
    return null;
  }

  return (
    <ul className="my-4 space-y-4">
      {resources.map((resource) => {
        const embed = embedUrl(resource);
        return (
          <li key={resource.id} className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{resource.title}</span>
              {onRemove !== undefined && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    onRemove(resource.id);
                  }}
                >
                  {t("resources.remove")}
                </Button>
              )}
            </div>
            {resource.type === "video" && embed !== null ? (
              <div className="aspect-video w-full overflow-hidden rounded-md bg-slate-900">
                <iframe
                  src={embed}
                  title={resource.title}
                  className="h-full w-full"
                  // `sandbox` en plus de l'allowlist de domaines (docs/11 §4) :
                  // deux barrières indépendantes, parce qu'un fournisseur de
                  // confiance sert tout de même du code que nous n'écrivons pas.
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            ) : resource.type === "image" && resource.file_id !== null ? (
              <img
                src={downloadUrl(resource.file_id)}
                alt={resource.title}
                className="max-h-96 w-full rounded-md object-contain"
              />
            ) : resource.type === "pdf" && resource.file_id !== null ? (
              <a
                href={downloadUrl(resource.file_id)}
                className="text-brand-600 hover:text-brand-700 text-sm underline underline-offset-2"
              >
                {t("resources.download")}
              </a>
            ) : (
              resource.url !== null && (
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:text-brand-700 text-sm underline underline-offset-2"
                >
                  {resource.url}
                </a>
              )
            )}
          </li>
        );
      })}
    </ul>
  );
}
